import { computed, inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { AuthSession } from "../../shared/models/auth-session";
import { resolveEffectiveRole } from "../../shared/models/enum-roles";
import { SessionRepositoryService } from "../../shared/services/session-repository.service";
import { BffAuthService } from "../rest-services/bff-auth.service";
import { SessionIdleService } from "./session-idle.service";

/**
 * Contexto central de autenticación de S-Stock Web.
 *
 * Flujo de login (cookie-based):
 *  1. LoginPage llama login(email, password)
 *  2. BffAuthService hace GET /v1/customer/login/{email}
 *  3. El BFF setea access_token, refresh_token y xsrf-token como cookies HttpOnly
 *  4. El body de respuesta devuelve customer + invUser (sin tokens)
 *  5. AuthSession se construye solo con datos de usuario (sin JWT)
 *  6. SessionRepositoryService persiste la sesión encriptada (AES-GCM) sin tokens
 *
 * El frontend nunca lee ni almacena tokens de acceso o refresco.
 */
@Injectable({ providedIn: "root" })
export class AuthService {
  private readonly bff         = inject(BffAuthService);
  private readonly sessionRepo = inject(SessionRepositoryService);
  private readonly router      = inject(Router);
  private readonly idle        = inject(SessionIdleService);

  private readonly _session = signal<AuthSession | null>(null);

  readonly session    = this._session.asReadonly();
  readonly isLoggedIn = computed(() => this._session()?.isLoggedIn === true);

  /** APP_INITIALIZER: restaura datos de usuario desde sessionStorage al arrancar. */
  async init(): Promise<void> {
    const stored = await this.sessionRepo.getSession();
    if (!stored?.isLoggedIn) return;
    this._session.set(stored);
    this.idle.start(() => this.logout());
  }

  /** Autentica contra el BFF. Las cookies de sesión las maneja el browser. */
  async login(email: string, password: string): Promise<void> {
    const { response } = await this.bff.login(email, password);
    const { customer, invUser } = response.data;

    const session = new AuthSession(
      invUser.email,
      customer,
      invUser,
      true,
      invUser.owner_id,
      resolveEffectiveRole(invUser.role, invUser.branches),
    );

    this._session.set(session);
    await this.sessionRepo.setSession(session);
    this.idle.start(() => this.logout());
    await this.router.navigate(["/app/home"]);
  }

  /** Cierra sesión: avisa al BFF (para limpiar cookies), luego limpia estado local. */
  async logout(): Promise<void> {
    this.idle.stop();
    await this.bff.logout();
    this._session.set(null);
    this.sessionRepo.clearSession();
    await this.router.navigate(["/login"]);
  }
}
